import React, { useState, useContext } from 'react';
import { Asset } from '../../../types/assets';
import {
  Button,
  Container,
  Grid,
  MenuItem,
  Select,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { EnquireFormData } from '../../../types/contact';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { TextField, FormControl } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { contactOptions } from '../../../utils/constants';
import { createEnquireMessage, getUser } from '../../../mutations/mutations';
import { toast } from 'react-toastify';
import { AuthContext } from '../../../auth/AuthContext';
import { formatCurrency } from '../../../utils/helpers';
import { QueryKeys } from '../../../react-query/QueryKeys';
import { useQuery } from 'react-query';
interface props {
  asset: Asset;
}

const Contact = ({ asset }: props) => {
  const isSmallScreen = useMediaQuery('(max-width: 1000px)');
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreeError, setAgreeError] = useState(false);
  const [areYouSure, setAreYouSure] = useState(false);
  const [areYouSureError, setAreYouSureError] = useState(false);
  const { user } = useContext(AuthContext);

  const { data: userData, isLoading } = useQuery(
    QueryKeys.PROFILE,
    () => getUser(user?.sub || ''),
    {
      enabled: !!user,
    }
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EnquireFormData>();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit: SubmitHandler<EnquireFormData> = async (data) => {
    setAgreeError(false);
    setAreYouSureError(false);
    try {
      let { option, message } = data;

      if (agreed && areYouSure) {
        setLoading(true);

        await createEnquireMessage(
          user.sub,
          user.email,
          user.name,
          asset?.company?.companyName,
          option,
          message,
          asset?.id
        );

        toast.success('Contact request sent successfully');
        setLoading(false);
        handleClose();
        reset();
      } else {
        setLoading(false);
        if (!agreed) setAgreeError(true);
        if (!areYouSure) setAreYouSureError(true);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  return (
    <Container maxWidth="xl">
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          md={8}
          style={{
            paddingLeft: isSmallScreen ? '20px' : '140px',
            paddingRight: isSmallScreen ? '20px' : '100px',
            marginBottom: '50px',
            paddingTop: '60px',
          }}
        >
          <h2>
            Want to know more about this business? Contact their team directly
          </h2>

          <Button variant="contained" onClick={handleClickOpen}>
            Enquire now
          </Button>
        </Grid>
      </Grid>

      <Dialog
        open={open}
        maxWidth={'md'}
        fullWidth={true}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Typography variant="h3">Contact Form</Typography>

            <FormControl fullWidth margin="normal">
              <TextField
                label="Full Name"
                variant="outlined"
                fullWidth
                disabled
                value={user?.name}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                disabled
                value={user?.email}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <TextField
                label="Phone"
                variant="outlined"
                fullWidth
                disabled
                value={userData?.user_metadata?.phone}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <TextField
                label="LinkedIn Profile"
                variant="outlined"
                fullWidth
                disabled
                value={userData?.user_metadata?.linkedInProfile}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Typography variant="h6">Please Select an option </Typography>
              <Controller
                name="option"
                control={control}
                defaultValue=""
                rules={{ required: 'Option is required' }}
                render={({ field }) => {
                  return (
                    <>
                      <FormControl fullWidth variant="outlined">
                        <Select
                          {...field}
                          error={!!errors?.option}
                          placeholder="Select an option"
                          id="option-type"
                        >
                          {contactOptions?.map((type: string) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  );
                }}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Controller
                name="message"
                control={control}
                defaultValue=""
                rules={{
                  required: 'Message is required',
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Message"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={5}
                    error={!!errors.message}
                    helperText={errors.message ? 'Message is required' : ''}
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Controller
                name="agreed"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={agreed}
                        color="secondary"
                        sx={{
                          '& .MuiSvgIcon-root': {
                            border: '1px solid #ccc',
                          },
                        }}
                        onChange={(e) => setAgreed(e.target.checked)}
                      />
                    }
                    label={` The minimum invetsment amount for this opportunity is
              $ ${
                asset?.investmentInfo?.minimumInvestment
                  ? formatCurrency(asset?.investmentInfo?.minimumInvestment)
                  : '0'
              } I confirm that any discussions with the asset are on the basis that I have the minimum funds to invest`}
                  />
                )}
              />
            </FormControl>
            {agreeError && (
              <Typography variant="body2" color="error">
                Please agree to the terms
              </Typography>
            )}
            <FormControl fullWidth margin="normal">
              <Controller
                name="areYouSure"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={areYouSure}
                        color="secondary"
                        sx={{
                          '& .MuiSvgIcon-root': {
                            border: '1px solid #ccc',
                          },
                        }}
                        onChange={(e) => setAreYouSure(e.target.checked)}
                      />
                    }
                    label="I have read and understood BlueSana Platform User Agreement (“PUA”)"
                  />
                )}
              />
            </FormControl>
            {areYouSureError && (
              <Typography variant="body2" color="error">
                Please confirm you have read and understood BlueSana Platform
                User Agreement (“PUA”).
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              Close
            </Button>
            <Button
              type="submit"
              disabled={loading}
              color="secondary"
              variant="contained"
            >
              Submit
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default Contact;
